import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';

import { ORDER_STATUS_COLORS, ORDER_STATUS_STRINGS } from '@domain/orderStatus';
import {
  ActionIcon,
  Badge,
  Center,
  Group,
  Loader,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconInfoSquareRoundedFilled } from '@tabler/icons-react';
import { priceToDecimal } from '@utils/price';

import { useGetAffiliationCountQuery } from '@api/clientsProfiles.api';
import {
  GetOrdersRequestParams,
  GetOrdersSortBy,
  useGetAffiliatedOrdersCountQuery,
  useGetAffiliatedOrdersQuery,
} from '@api/orders.api';

import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';
import { AffiliatedOrder } from '@interfaces/orders.interface';

export default function AffiliatedOrders() {
  const [searchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetOrdersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<AffiliatedOrder>
  >({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetOrdersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================

  const {
    data: affiliatedUsersCount = 0,
    isLoading: isLoadingAffiliatedUsersCount,
  } = useGetAffiliationCountQuery();

  // Get initial orders count
  const { data: ordersCount = { count: 0 }, isLoading: isLoadingOrdersCount } =
    useGetAffiliatedOrdersCountQuery(searchQuery);

  const { data = [], isLoading } = useGetAffiliatedOrdersQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    if (!isLoadingAffiliatedUsersCount) {
      setLayoutProps({
        title: `Lista ordini utenti affiliati (${affiliatedUsersCount} utenti affiliati)`,
      });
    } else {
      setLayoutProps({
        title: 'Lista ordini utenti affiliati',
      });
    }
  }, [setLayoutProps, isLoadingAffiliatedUsersCount]);

  return isLoading || isLoadingOrdersCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : (
    <>
      <Group mb="lg">
        <SearchBar
          placeholder="Cerca numero ordine"
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>
      <DataTable
        striped
        withTableBorder
        noRecordsText="Nessun ordine trovato per i filtri selezionati"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'date',
            title: 'Data',
            sortable: true,
            render: (record) =>
              new Date(record.date).toLocaleDateString('IT-it'),
          },
          {
            accessor: 'id',
            title: 'Numero ordine',
            sortable: true,
          },
          {
            accessor: 'total',
            title: 'Totale',
            render: (record) => '€ ' + priceToDecimal(record.total),
          },
          {
            accessor: 'sellerCommission',
            title: 'Provvigione',
            render: (record) =>
              '€ ' + priceToDecimal(record.sellerCommission || 0),
          },
          {
            accessor: 'invoicingName',
            title: 'Cliente',
          },
          {
            accessor: 'status',
            title: 'Stato',
            render: (record) => (
              <Badge
                color={
                  ORDER_STATUS_COLORS[
                    record.status as keyof typeof ORDER_STATUS_COLORS
                  ]
                }
              >
                {
                  ORDER_STATUS_STRINGS[
                    record.status as keyof typeof ORDER_STATUS_STRINGS
                  ]
                }
              </Badge>
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Tooltip label="Dettagli ordine">
                <ActionIcon
                  variant="subtle"
                  component={Link}
                  to={`/ordini-affiliati/${record.id}`}
                >
                  <IconInfoSquareRoundedFilled />
                </ActionIcon>
              </Tooltip>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={ordersCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
